export type FaceLayoutEntry = {
  userId: string;
  vx: number;
  vy: number;
  vz: number;
  visible: boolean;
  scale: number;
  /** World-space head (Unity) — used when browser recomputes vx/vy from view-projection. */
  wx?: number;
  wy?: number;
  wz?: number;
};

/** Column-major 4×4 × homogeneous point (matches Unity Matrix4x4 * column vector). */
function mulMat4Vec4Col(m: number[], x: number, y: number, z: number, w: number) {
  return {
    x: m[0] * x + m[4] * y + m[8] * z + m[12] * w,
    y: m[1] * x + m[5] * y + m[9] * z + m[13] * w,
    z: m[2] * x + m[6] * y + m[10] * z + m[14] * w,
    w: m[3] * x + m[7] * y + m[11] * z + m[15] * w,
  };
}

function extractVp16ColMajor(data: Record<string, unknown>): number[] | null {
  if (data.browserFacePinViewport !== true) {
    return null;
  }
  const keys = [
    'facePinVp00',
    'facePinVp01',
    'facePinVp02',
    'facePinVp03',
    'facePinVp10',
    'facePinVp11',
    'facePinVp12',
    'facePinVp13',
    'facePinVp20',
    'facePinVp21',
    'facePinVp22',
    'facePinVp23',
    'facePinVp30',
    'facePinVp31',
    'facePinVp32',
    'facePinVp33',
  ] as const;
  const out: number[] = [];
  for (const k of keys) {
    const v = data[k];
    if (typeof v !== 'number' || Number.isNaN(v)) {
      return null;
    }
    out.push(v);
  }
  return out;
}

/**
 * Recompute Unity-style normalized viewport (vx, vy) from world head + view-projection.
 * Keeps vz, visible, scale from Unity (sorting + visibility stay consistent with Unity rules).
 */
export function applyBrowserFacePinViewport(faces: FaceLayoutEntry[], vpColMajor: number[]): FaceLayoutEntry[] {
  if (vpColMajor.length !== 16) {
    return faces;
  }
  return faces.map((f) => {
    const wx = f.wx ?? 0;
    const wy = f.wy ?? 0;
    const wz = f.wz ?? 0;
    const clip = mulMat4Vec4Col(vpColMajor, wx, wy, wz, 1);
    if (Math.abs(clip.w) < 1e-8) {
      return f;
    }
    const ndcX = clip.x / clip.w;
    const ndcY = clip.y / clip.w;
    let vx = ndcX * 0.5 + 0.5;
    let vy = ndcY * 0.5 + 0.5;
    // Unity viewport Y is 0 at bottom; GL-style NDC has +Y up — flip to match WorldToViewportPoint.
    vy = 1 - vy;
    return { ...f, vx, vy };
  });
}

/** Parse Unity JsonUtility VideoFaceLayout payload and optionally apply browser-side projection. */
export function normalizeVideoFaceLayoutFromUnityPayload(data: unknown): FaceLayoutEntry[] {
  if (data == null || typeof data !== 'object') {
    return [];
  }
  const rec = data as Record<string, unknown>;
  const rawFaces = rec.faces;
  if (!Array.isArray(rawFaces)) {
    return [];
  }
  let faces = rawFaces as FaceLayoutEntry[];
  const vp = extractVp16ColMajor(rec);
  const hasWorldHead = faces.some(
    (f) => (f.wx ?? 0) !== 0 || (f.wy ?? 0) !== 0 || (f.wz ?? 0) !== 0
  );
  if (vp && hasWorldHead) {
    faces = applyBrowserFacePinViewport(faces, vp);
  }
  return faces;
}
